import {IvrActionType, SubmenuSettings} from "@/api";
import {
    ButtonPath,
    ButtonPathString,
    UpdateButton,
    appendToButtonPathString,
    buttonPathStringToNumberArray,
    buttonPathToString,
} from "./tools";
import {DeepPartial, Intersection} from "@/tools";
import {
    UpdateIvrProfileMutation,
    UpdateIvrProfileMutationVariables,
    useUpdateIvrProfileMutation,
} from "@/components/ivr/profiles/mutation.generated";
import {Form} from "@/utils/Form";
import {UpdateIvrMenuMedia} from "./MediaTable";

export type IvrMediaErrorLocation = "menuMedia" | "greetings" | "connectionPrompt";

/** Fields that are common between the root profile and submenus. */
type CrossPathFields = Omit<Intersection<SubmenuSettings, UpdateIvrProfileMutationVariables>, "buttons" | "menuMedia">;

const frontendOrder = [1, 2, 3, 4, 5, 6, 7, 8, 9, 0, 10, 11, 12] as const;

/**
 * Adjusts the action type of a button based on the target number ID.
 *
 * The UI uses Switch to for any target, but on the backend we need Forward or ForwardToIvr.
 */
function adjustForwardTo(button: UpdateButton, ivrNumberIds: number[]) {
    if (button.actionType === IvrActionType.ForwardToIvr) {
        if (!ivrNumberIds.includes(button.targetNumberId)) {
            button.actionType = IvrActionType.Forward;
        }
    } else if (button.actionType === IvrActionType.Forward) {
        // if the button is a forward, but the target number is an IVR, change it to forward to auto attendant
        if (ivrNumberIds.includes(button.targetNumberId)) {
            button.actionType = IvrActionType.ForwardToIvr;
        }
    }
}
export class UpdateProfileForm extends Form<UpdateIvrProfileMutation, UpdateIvrProfileMutationVariables> {
    readonly profileId: number;

    /// only for clearing the media cache in case new audio is uploaded
    readonly domainId: number;

    private ivrNumberIds: number[];
    setIvrNumberIds: (values: number[]) => void;

    constructor(
        profileId: number,
        domainId: number,
        initialData: UpdateIvrProfileMutationVariables,
        ivrNumberIds: number[],
    ) {
        super("updateIvrProfile");
        this.hooks(useUpdateIvrProfileMutation, initialData);
        this.profileId = profileId;
        this.domainId = domainId;
        this.ivrNumberIds = ivrNumberIds;
    }

    /**
     * Get the buttons that are correctly ordered for the frontend.
     * @param path The path of the buttons to get.
     * @param buttons The buttons to order.
     * @returns The ordered buttons along with their frontend index.
     * */
    private getFrontendOrderedButtons(buttons: UpdateButton[]): [UpdateButton, (typeof frontendOrder)[number]][] {
        const orderedButtons: [UpdateButton, (typeof frontendOrder)[number]][] = [];
        const copiedButtons = structuredClone(buttons);
        for (let i = 0; i < copiedButtons.length; i++) {
            const button = copiedButtons[frontendOrder[i]];
            orderedButtons.push([button, frontendOrder[i]]);
        }
        return orderedButtons;
    }

    getSubmenuIndexForPath(path: ButtonPathString): number {
        if (path === "") return undefined;
        for (let i = 0; i < this.values.submenus.length; i++) {
            if (buttonPathToString(this.values.submenus[i].path) === path) return i;
        }
        return undefined;
    }

    /**
     * Check if a submenu or their children have an error.
     * @param path The path of the submenu to check.
     * @returns True if the submenu or their children have an error, false otherwise.
     * */
    submenuHasError(path: ButtonPathString): boolean {
        for (const error of this.errors) {
            const errorContext = JSON.parse(JSON.parse(error.context)["context"]);
            const errorPath: string = errorContext.path?.replace(/ -> /g, "_") ?? "";
            if (errorPath.startsWith(path)) return true;
        }

        return false;
    }

    /**
     * Get the correctly ordered buttons for the frontend.
     * @param path The path of the buttons to get.
     * @returns The ordered buttons along with their frontend index.
     * */
    getButtonsForFrontend(path: ButtonPathString): [UpdateButton, (typeof frontendOrder)[number]][] {
        if (path === "") return this.getFrontendOrderedButtons(this.values.buttons);
        return this.getFrontendOrderedButtons(
            this.values.submenus.find((submenu) => buttonPathToString(submenu.path) === path).buttons,
        );
    }

    /**
     * Get the menu media (or greetings) for a path.
     * @param path The path to get the media for.
     * @param type The type of media to get.
     * @returns The media for the path.
     **/
    getMenuMedia(path: ButtonPathString, type: "menuMedia" | "greetings"): UpdateIvrMenuMedia[] {
        if (type === "greetings") return this.values.greetings;

        if (path === "") return this.values.menuMedia;

        return this.values.submenus?.find((submenu) => buttonPathToString(submenu.path) === path)?.menuMedia;
    }

    /**
     * Remove all media errors.
     * */
    private removeMenuMediaErrors() {
        const errors = structuredClone(this.errors).filter((error) => {
            if (error.type !== "value_error.invalid_audio_ivr") return true;

            const errorLocation = error.location.at(-1) === "buttons" ? "connectionPrompt" : error.location.at(-1);
            return errorLocation === "connectionPrompt";
        });

        this.setErrors(errors);
    }

    /**
     * Update the menu media for a path.
     * @param path The path to update the media for.
     * @param newMenuMedia The new media to set.
     * */
    updateMenuMedia(path: ButtonPathString, newMenuMedia: UpdateIvrMenuMedia[], type: "menuMedia" | "greetings") {
        const newData = {} as DeepPartial<UpdateIvrProfileMutationVariables>;

        this.removeMenuMediaErrors();

        if (type === "greetings") {
            newData.greetings = newMenuMedia;
        } else {
            if (path === "") {
                newData.menuMedia = newMenuMedia;
            } else {
                const newSubmenus = structuredClone([...this.values.submenus]);
                newSubmenus.find((submenu) => buttonPathToString(submenu.path) === path).menuMedia = newMenuMedia;
                newData.submenus = newSubmenus;
            }
        }

        this.updateValues(newData);
    }

    getCrossPathField<T extends keyof CrossPathFields>(path: ButtonPathString, field: T): CrossPathFields[T] {
        if (path === "") return this.values[field] as CrossPathFields[T];

        const submenu = this.values.submenus.find((submenu) => buttonPathToString(submenu.path) === path);
        return submenu[field] as CrossPathFields[T];
    }

    setCrossPathField<T extends keyof CrossPathFields, V extends CrossPathFields[T]>(
        path: ButtonPathString,
        field: T,
        value: V,
    ): void {
        const newData = {} as DeepPartial<UpdateIvrProfileMutationVariables>;

        if (path === "") {
            newData[field] = value;
        } else {
            newData.submenus = structuredClone(this.values.submenus);
            const submenu = newData.submenus.find((submenu) => buttonPathToString(submenu.path) === path);
            if (!submenu) {
                console.error("Tried to set field on non-existing submenu", path, field, value);
                return;
            }

            submenu[field] = value;
        }

        this.updateValues(newData);
    }

    /**
     * Get the buttons for a path. Only used in the form.
     * @param path The path to get the buttons for.
     * @returns The buttons for the path.
     * */
    private getButtons(path: ButtonPathString): UpdateButton[] {
        if (path === "") return this.values.buttons;
        return this.values.submenus?.find((submenu) => buttonPathToString(submenu.path) === path)?.buttons;
    }

    /**
     * Updates one button for a path.
     * @param path The path to update the button for.
     * @param index The index of the button to update.
     * @param value The new button to set.
     * */
    updateButtonFor(path: ButtonPathString, index: number, value: DeepPartial<UpdateButton>): void {
        const newButtons = structuredClone(this.getButtons(path));
        const oldButton = structuredClone(newButtons[index]);
        const newButton = structuredClone({...newButtons[index], ...value});
        newButtons[index] = newButton;

        const newData = {} as DeepPartial<UpdateIvrProfileMutationVariables>;
        newData.submenus = structuredClone(this.values.submenus);

        // first create new menu if needed, or delete if changed to not be a submenu
        if (oldButton.actionType !== IvrActionType.Submenu && newButton.actionType === IvrActionType.Submenu) {
            const newSubmenus = this.addSubmenu(
                buttonPathStringToNumberArray(path, index),
                newData.submenus as SubmenuSettings[],
            );

            if (newSubmenus !== undefined) {
                newData.submenus = newSubmenus;
            } else {
                console.error("Failed to add submenu", path, index);
                return;
            }
        } else if (oldButton.actionType === IvrActionType.Submenu && newButton.actionType !== IvrActionType.Submenu) {
            newData.submenus = this.removeSubmenu(appendToButtonPathString(path, index));
        }

        // then update the buttons
        // (if we reverse the order, the submenu's buttons change would be lost due to copy-on-write above)
        if (path === "") {
            newData.buttons = newButtons;
        } else {
            const submenu = newData.submenus.find((submenu) => buttonPathToString(submenu.path) === path);
            submenu.buttons = newButtons;
        }

        this.updateValues(newData);
    }

    /**
     * Remove a submenu and all submenus of it. This way the form values are kept clean.
     * @param path The path to remove the submenu for.
     * @returns The new submenus.
     * */
    private removeSubmenu(path: ButtonPathString): SubmenuSettings[] {
        const newSubmenus = [...this.values.submenus].filter(
            (submenu) => !buttonPathToString(submenu.path).startsWith(path),
        );
        return newSubmenus;
    }

    /**
     * Add a submenu to a path.
     * @param path The path to add the submenu to.
     * @returns The new submenus or undefined if already exists.
     * */
    private addSubmenu(path: ButtonPath, submenus?: SubmenuSettings[]): SubmenuSettings[] | undefined {
        if (submenus.find((submenu) => buttonPathToString(submenu.path) === buttonPathToString(path))) return undefined;

        const newSubmenu: SubmenuSettings = {
            path: path,
            buttons: this.getNewButtons(),
            menuMedia: [],
            instantConnect: false,
            timeout: 60,
        };

        const newSubmenus = structuredClone(submenus);
        newSubmenus.push(newSubmenu);

        return newSubmenus;
    }

    /**
     * Get a new array of empty buttons.
     * @returns The new buttons.
     * */
    private getNewButtons(): UpdateButton[] {
        const newButtons = [];
        const getEmptyButton = (): UpdateButton => ({
            actionType: null,
            connectionPrompt: {
                unset: false,
            },
        });
        for (let i = 0; i < 13; i++) {
            newButtons.push(getEmptyButton());
        }
        return newButtons;
    }

    override get filteredVariables(): UpdateIvrProfileMutationVariables {
        const copy = structuredClone(this.values);
        const media = [] as UpdateIvrMenuMedia[];

        media.push(...copy.menuMedia.concat(copy.greetings));
        media.push(...copy.submenus.flatMap((submenu) => submenu.menuMedia));

        // if 'switch to' refers to an ivr, change it to 'switch to auto attendant'
        for (const button of copy.buttons) {
            adjustForwardTo(button, this.ivrNumberIds);
            if (!(button.connectionPrompt?.id || button.connectionPrompt?.file) && !button.connectionPrompt?.unset) {
                delete button.connectionPrompt;
            }
        }

        // same for submenus, and also remove 'back to greeting'
        for (const button of copy.submenus.flatMap((submenu) => submenu.buttons)) {
            adjustForwardTo(button, this.ivrNumberIds);
            if (button.actionType === IvrActionType.BackToGreeting) {
                button.actionType = undefined;
            }
            if (!(button.connectionPrompt?.id || button.connectionPrompt?.file)) {
                delete button.connectionPrompt;
            }
        }

        return copy;
    }

    override submit(): Promise<boolean> {
        return super.submit({
            evictCaches: [
                {
                    fieldName: "media",
                    args: {
                        domainId: this.domainId,
                    },
                },
            ],
        });
    }
}
